import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';

interface DesktopContextType {
  wallpaper: string;
  setWallpaper: (wallpaper: string) => void;
  theme: string;
  setTheme: (theme: string) => void;
  settings: any;
  updateSettings: (newSettings: any) => void;
}

const DesktopContext = createContext<DesktopContextType | undefined>(undefined);

export const useDesktop = () => {
  const context = useContext(DesktopContext);
  if (!context) {
    throw new Error('useDesktop must be used within a DesktopProvider');
  }
  return context;
};

export const DesktopProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [wallpaper, setWallpaperState] = useState(() => {
    const saved = localStorage.getItem('portfolio-wallpaper');
    return saved || 'cybersecurity';
  });
  
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('portfolio-theme');
    return saved || 'dark';
  });

  const [settings, setSettingsState] = useState(() => {
    const saved = localStorage.getItem('portfolio-settings');
    return saved ? JSON.parse(saved) : {
      animations: true,
      soundEffects: false,
      notifications: true,
      autoSave: true,
      darkMode: true,
      fontSize: 14,
      volume: 50,
      terminalTheme: 'matrix',
      language: 'en'
    };
  });

  const setWallpaper = (newWallpaper: string) => {
    setWallpaperState(newWallpaper);
    localStorage.setItem('portfolio-wallpaper', newWallpaper);
  };

  const updateSettings = (newSettings: any) => {
    const updatedSettings = { ...settings, ...newSettings };
    setSettingsState(updatedSettings);
    localStorage.setItem('portfolio-settings', JSON.stringify(updatedSettings));
  };

  const value = {
    wallpaper,
    setWallpaper,
    theme,
    setTheme,
    settings,
    updateSettings
  };

  return (
    <DesktopContext.Provider value={value}>
      {children}
    </DesktopContext.Provider>
  );
};
